/**
 * SessionDetailPage — detalle de una sesión específica.
 *
 * Sprint 6 Bloque 4. Renderiza:
 * - Header con día + fecha + tipo de sesión.
 * - Bloque "Estructura" con warmup/main/cooldown del session_structure_json.
 * - Bloque "Tu actividad" si actual_activity_id != null (placeholder
 *   simple — en Bloque 5+ enriquecemos con datos reales del Activity).
 * - Acciones (sólo si planned): "Marcar completada" + "Saltar".
 *   - Skip → modal con razones logística/física.
 *   - Complete → confirma y patch directo.
 *
 * Como NO tenemos endpoint GET /training-plans/active/sessions/<id>,
 * recuperamos la sesión vía getActivePlan() y filtramos por id. Más
 * costoso pero evita añadir endpoint + tests por una optimización
 * marginal en este sprint.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { getOnboardingStatus } from "@/api/onboarding";
import { getActivePlan, patchSession, submitVamResult } from "@/api/training";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { EffortSelector } from "@/components/training/EffortSelector";
import { t } from "@/i18n/es";
import { labelForRecommendedSurface } from "@/lib/surfaceLabel";
import { labelForVamTestKind } from "@/lib/vamTestKindLabel";
import { TestVamProtocolBlock } from "@/pages/Plan/components/TestVamProtocolBlock";
import {
  VISUAL_STATE_BADGE_CLASSES,
  VISUAL_STATE_I18N_KEY,
  VISUAL_STATE_ICONS,
  getSessionVisualState,
} from "@/utils/sessionVisualState";
import {
  ApiError,
  type PatchSessionPayload,
  type SessionFeeling,
  type TrainingSession,
} from "@/types/api";

// v14_2 §8: opciones del selector de sensaciones (orden de peor→mejor o
// mejor→peor; aquí mejor→peor para lectura natural).
const FEELING_OPTIONS: { value: SessionFeeling; labelKey: string }[] = [
  { value: "muy_bien", labelKey: "feelingMuyBien" },
  { value: "bien", labelKey: "feelingBien" },
  { value: "regular", labelKey: "feelingRegular" },
  { value: "mal", labelKey: "feelingMal" },
];

const SESSION_TYPE_LABELS = {
  rest: "sessionTypeRest",
  z2: "sessionTypeZ2",
  z2_fartlek: "sessionTypeZ2Fartlek",
  caco_z2: "sessionTypeCacoZ2",
  caco_plus: "sessionTypeCacoPlus",
  series_cortas: "sessionTypeSeriesCortas",
  series_medias: "sessionTypeSeriesMedias",
  tempo_continuo: "sessionTypeTempoContinuo",
  larga_progresiva: "sessionTypeLargaProgresiva",
  rodaje_z3: "sessionTypeRodajeZ3",
  tempo_largo: "sessionTypeTempoLargo",
  taper_combo: "sessionTypeTaperCombo",
  cuestas_v15: "sessionTypeCuestasV15",
  test_vam: "sessionTypeTestVam",
  // v13 §1: caco_recovery eliminado. rod_z2_corto se conserva (sustituta
  // por fatiga, mismo día).
  rod_z2_corto: "sessionTypeRodZ2Corto",
  // S10.3: rodaje largo trail (inyectado en planes trail/mixto).
  long_run_trail: "sessionTypeLongRunTrail",
  // Pre-flight F16: rodaje suave por sensaciones previo al Test VAM.
  rodaje_previo: "sessionTypeRodajePrevio",
  // v16.3 C.1 (Q1 Alberto): activación pre-carrera del día previo a race_date.
  activacion_pre_carrera: "sessionTypeActivacionPreCarrera",
} as const;

interface Toast {
  id: number;
  message: string;
  kind: "success" | "error";
}

let toastSeq = 0;

/** Sprint 10 S10.1b: mapea ApiError del PATCH postpone a copy i18n.
 *
 * Códigos REALES del backend S10.1 (commit 31b2f18):
 * - `cannot_postpone_test_vam` (400)
 * - `session_not_planned` (400) — sesión ya completada o saltada.
 *   Nota: el spec menciona `cannot_postpone_completed_session` pero
 *   el backend en realidad emite `session_not_planned` (también cubre
 *   "partial"). Soportamos ambos por defensa.
 * - `session_already_passed` (400) — fecha ya pasada.
 * - `cannot_postpone_preserves_race_date` (409).
 * - HTTP 403 (forbidden) — defensivo; el backend usa 404
 *   `session_not_found` para no leakear existencia de sesiones de
 *   otros users, pero soportamos 403 por si un futuro middleware lo
 *   emite. */
function mapPostponeError(err: unknown, raceDate: string | null): string {
  if (err instanceof ApiError) {
    if (err.code === "cannot_postpone_test_vam") {
      return t.plan.postponeErrorTestVam;
    }
    if (
      err.code === "cannot_postpone_completed_session"
      || err.code === "session_not_planned"
      || err.code === "session_already_passed"
    ) {
      return t.plan.postponeErrorCompleted;
    }
    if (err.code === "cannot_postpone_preserves_race_date") {
      return t.plan.postponeErrorRaceOverflow.replace(
        "{race_date}",
        raceDate ? formatRaceDate(raceDate) : "",
      );
    }
    if (err.status === 403) {
      return t.plan.postponeErrorForbidden;
    }
  }
  return t.plan.postponeErrorGeneric;
}

function formatRaceDate(iso: string): string {
  // Mismo formato visual que el header de la sesión (es-ES, día + mes).
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Sprint 10 S10.2b: mapea ApiError del PATCH report_fatigue a copy i18n.
 *
 * Códigos REALES del backend S10.2 (commit 01619ca):
 * - `cannot_fatigue_test_vam` (400)
 * - `cannot_fatigue_rest_session` (400)
 * - `cannot_fatigue_past_session` (400)
 * - `cannot_fatigue_future_session` (400) — más allá de today+1d
 * - `session_not_planned` (400) — ya completed/skipped (no-fatigue)
 * - `already_reported_fatigue` (409) — idempotencia
 *
 * El 404 cross-user no llega aquí porque el endpoint devuelve 404
 * `session_not_found` antes de invocar `report_fatigue` (mismo patrón
 * que postpone). */
function mapFatigueError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "cannot_fatigue_test_vam") return t.plan.fatigueErrorTestVam;
    if (err.code === "cannot_fatigue_rest_session") return t.plan.fatigueErrorRest;
    if (err.code === "cannot_fatigue_past_session") return t.plan.fatigueErrorPast;
    if (err.code === "cannot_fatigue_future_session")
      return t.plan.fatigueErrorFarFuture;
    if (err.code === "session_not_planned") return t.plan.fatigueErrorNotPlanned;
    if (err.code === "already_reported_fatigue")
      return t.plan.fatigueErrorAlreadyReported;
  }
  return t.plan.fatigueErrorGeneric;
}

/** ISO YYYY-MM-DD de hoy local (sin TZ). El backend usa Date.today()
 *  server-side; nosotros calculamos local porque las validaciones UI
 *  son optimistas — el backend re-valida. */
function _todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function _addDaysIso(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** v11 §1: convierte un ritmo "mm:ss" (ej. "5:30") a segundos/km. Devuelve
 *  null si el formato no es válido. Acepta opcionalmente "m:ss" (1-2 dígitos
 *  de minutos) y exige ss en [0,59]. */
function _parsePaceMmSs(value: string): number | null {
  const m = /^(\d{1,2}):([0-5]\d)$/.exec(value.trim());
  if (!m) return null;
  const minutes = Number(m[1]);
  const seconds = Number(m[2]);
  return minutes * 60 + seconds;
}

/** v14_2 §8: inverso de `_parsePaceMmSs` — segundos/km → "m:ss" (para
 *  mostrar el ritmo reportado en el bloque de resultado). */
function _formatPaceSeconds(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** Sprint 10 S10.1b: metadata mínima del plan que SessionDetailPage
 *  necesita para el flow de Posponer (warning condicional + copy del
 *  error de overflow). Lo guardamos aparte del `session` porque el
 *  state actual filtra solo la sesión y descartaba el plan padre. */
interface PlanMeta {
  raceDistance: string;
  raceDate: string;
}

export function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<TrainingSession | null>(null);
  const [planMeta, setPlanMeta] = useState<PlanMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  // Sprint 12 S12.7 (motor v9.2 §1): tras la consolidación "3 tipos:
  // Posponer / Skip físico / Fatiga", el skip solo cubre el caso
  // físico (dolor/lesión). El reason se fija constante — antes era
  // un useState para alternar logistics/physical, ahora siempre
  // "physical".
  const [skipNotes, setSkipNotes] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  // VAM modal state (Sprint 6 Bloque 5)
  const [vamModalOpen, setVamModalOpen] = useState(false);
  const [vamDistance, setVamDistance] = useState("");
  const [vamHrAvg, setVamHrAvg] = useState("");
  const [vamHrMax, setVamHrMax] = useState("");
  // v11 §1: ritmo medio opcional, formato mm:ss.
  const [vamPace, setVamPace] = useState("");
  const [vamError, setVamError] = useState<string | null>(null);
  // Sprint 10 S10.1b: estado del modal de Posponer. Sin reason ni notes
  // (v7.1 §8 "sin pedir motivo"). `postponeError` se muestra inline en
  // el modal para los códigos 4xx específicos del backend.
  const [postponeModalOpen, setPostponeModalOpen] = useState(false);
  const [postponeError, setPostponeError] = useState<string | null>(null);
  // Sprint 10 S10.2b: estado del modal de Reportar Fatiga. Sin reason
  // ni notes (v7.1 §9 "sin pedir motivo"). `hasWatch` se fetchea desde
  // /onboarding/status para discriminar el sub-copy del modal (atleta
  // con reloj recibe instrucción Z2 cuantitativa, sin reloj cualitativa).
  const [fatigueModalOpen, setFatigueModalOpen] = useState(false);
  const [fatigueError, setFatigueError] = useState<string | null>(null);
  const [hasWatch, setHasWatch] = useState<boolean | null>(null);
  // v14_2 §8: modal post-sesión + sus 5 campos (todos opcionales).
  const [postSessionModalOpen, setPostSessionModalOpen] = useState(false);
  const [psHrAvg, setPsHrAvg] = useState("");
  const [psHrMax, setPsHrMax] = useState("");
  const [psPace, setPsPace] = useState("");
  const [psEffort, setPsEffort] = useState<number | null>(null);
  const [psFeeling, setPsFeeling] = useState<SessionFeeling | null>(null);
  const [postSessionError, setPostSessionError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Bug #2 móvil: bloquear el scroll del body mientras CUALQUIER modal esté
  // abierto, para que el fondo no se mueva (scroll chaining) bajo el modal.
  useBodyScrollLock(
    vamModalOpen
      || skipModalOpen
      || postponeModalOpen
      || fatigueModalOpen
      || postSessionModalOpen,
  );

  const showToast = useCallback(
    (message: string, kind: "success" | "error" = "success") => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((tt) => tt.id !== id));
      }, 5000);
    },
    [],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Sprint 13 S13.10a: getActivePlan devuelve { plan, ...flags }.
      const { plan } = await getActivePlan();
      if (!plan) {
        setSession(null);
        setPlanMeta(null);
        return;
      }
      const parsedId = Number.parseInt(sessionId ?? "", 10);
      const found =
        plan.weeks
          .flatMap((w) => w.sessions)
          .find((s) => s.id === parsedId) ?? null;
      setSession(found);
      // Sprint 10 S10.1b: persistimos race_distance + race_date para el
      // modal de Posponer (warning condicional + copy del error 409).
      setPlanMeta({
        raceDistance: plan.race_distance,
        raceDate: plan.race_date,
      });
    } catch {
      setSession(null);
      setPlanMeta(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Sprint 10 S10.2b: fetch onboarding status para conocer has_watch
  // del perfil. Necesario para el sub-copy del modal de fatiga. Lazy
  // y fail-safe: si falla, hasWatch queda null y el modal usa el copy
  // sin reloj (más conservador).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getOnboardingStatus();
        if (cancelled) return;
        setHasWatch(status.profile?.has_watch ?? null);
      } catch {
        if (!cancelled) setHasWatch(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCompleteManual() {
    if (!session) return;
    setSubmitting(true);
    try {
      const result = await patchSession(session.id, {
        action: "complete_manual",
        notes: completeNotes || undefined,
      });
      setSession(result.session);
      showToast(t.plan.toastSessionCompleted, "success");
    } catch {
      showToast(t.plan.toastError, "error");
    } finally {
      setSubmitting(false);
    }
  }

  // v16.3 §2: gate del botón "Marcar completada" decidido POR SESIÓN, no
  // por cuenta. Si la sesión YA tiene `actual_activity_id` (auto_link la
  // asoció a una actividad Strava), los datos vienen verbatim del import
  // → completamos directo SIN modal. Si NO la tiene (manual o sin match
  // Strava), abrimos el modal para captura manual de FC/ritmo/sensaciones.
  //
  // Esto corrige el v14_2 §8 que decidía a nivel de cuenta: cuando un
  // atleta con Strava conectado completaba una sesión que no estaba
  // sincronizada (entrenó fuera, Strava no encontró match), se completaba
  // directo sin capturar nada — quedaban datos vacíos. El nuevo gate
  // garantiza que cada sesión con datos viene de su fuente correcta.
  function onCompleteClick() {
    if (!session) return;
    if (session.actual_activity_id != null) {
      void onCompleteManual();
      return;
    }
    setPostSessionError(null);
    setPostSessionModalOpen(true);
  }

  // v14_2 §8: "Guardar entrenamiento". Completa la sesión enviando los
  // campos rellenos (los vacíos se omiten → NULL en backend). Guarda
  // AUNQUE estén todos vacíos (completar es lo primario). Valida en
  // cliente (mismos rangos que el backend) antes de enviar.
  async function onSavePostSession() {
    if (!session) return;
    setPostSessionError(null);

    const payload: PatchSessionPayload = {
      action: "complete_manual",
      notes: completeNotes || undefined,
    };

    // FC media / máx: opcionales; enteros 40-230 si vienen.
    const hrFields: [string, "actual_avg_hr" | "actual_max_hr"][] = [
      [psHrAvg, "actual_avg_hr"],
      [psHrMax, "actual_max_hr"],
    ];
    for (const [raw, key] of hrFields) {
      const trimmed = raw.trim();
      if (trimmed !== "") {
        const n = Number(trimmed);
        if (!Number.isInteger(n) || n < 40 || n > 230) {
          setPostSessionError(t.plan.postSessionInvalid);
          return;
        }
        payload[key] = n;
      }
    }

    // Ritmo: opcional; mm:ss → segundos (MISMO parser que el Test VAM).
    const paceTrim = psPace.trim();
    if (paceTrim !== "") {
      const secs = _parsePaceMmSs(paceTrim);
      if (secs === null || secs < 120 || secs > 900) {
        setPostSessionError(t.plan.postSessionInvalid);
        return;
      }
      payload.actual_avg_pace_seconds = secs;
    }

    // Esfuerzo (selector 1-5) y sensaciones (selector): ya válidos o null.
    if (psEffort !== null) payload.perceived_effort = psEffort;
    if (psFeeling !== null) payload.feeling = psFeeling;

    setSubmitting(true);
    try {
      const result = await patchSession(session.id, payload);
      setSession(result.session);
      setPostSessionModalOpen(false);
      showToast(t.plan.toastSessionCompleted, "success");
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setPostSessionError(t.plan.postSessionInvalid);
      } else {
        showToast(t.plan.toastError, "error");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // v14_2 §8: cerrar sin guardar. La sesión NO se completa (sigue planned);
  // mostramos el nudge y permitimos cerrar igual.
  function onClosePostSession() {
    setPostSessionModalOpen(false);
    setPostSessionError(null);
    showToast(t.plan.postSessionCloseNudge, "success");
  }

  async function onSubmitVam() {
    if (!session) return;
    setVamError(null);

    // Validación cliente
    const distNum = Number(vamDistance.trim());
    if (
      !vamDistance.trim()
      || !Number.isFinite(distNum)
      || distNum <= 0
    ) {
      setVamError(t.plan.testVamErrorDistance);
      return;
    }
    // v11 §1: FC media y FC máx OBLIGATORIAS — bloquear si falta cualquiera.
    const hrAvgTrim = vamHrAvg.trim();
    const hrMaxTrim = vamHrMax.trim();
    if (!hrAvgTrim || !hrMaxTrim) {
      setVamError(t.plan.testVamErrorHrRequired);
      return;
    }
    const hrAvgNum = Number(hrAvgTrim);
    if (!Number.isFinite(hrAvgNum) || hrAvgNum < 40 || hrAvgNum > 230) {
      setVamError(t.plan.testVamErrorHrRange);
      return;
    }
    const hrMaxNum = Number(hrMaxTrim);
    if (!Number.isFinite(hrMaxNum) || hrMaxNum < 40 || hrMaxNum > 230) {
      setVamError(t.plan.testVamErrorHrRange);
      return;
    }
    // v11 §1: ritmo medio OPCIONAL en mm:ss → segundos. Si se escribió pero
    // el formato/rango no es válido, bloquear con mensaje claro.
    const paceTrim = vamPace.trim();
    let paceSeconds: number | undefined;
    if (paceTrim) {
      const parsed = _parsePaceMmSs(paceTrim);
      if (parsed === null || parsed < 120 || parsed > 900) {
        setVamError(t.plan.testVamErrorPace);
        return;
      }
      paceSeconds = parsed;
    }

    setSubmitting(true);
    try {
      const result = await submitVamResult(session.id, {
        distance_meters: distNum,
        hr_avg: Math.round(hrAvgNum),
        hr_max: Math.round(hrMaxNum),
        ...(paceSeconds !== undefined
          ? { avg_pace_seconds_per_km: paceSeconds }
          : {}),
      });
      // Actualizar la sesión con el resultado.
      setSession(result.session);
      // v16.1 §8 sub-bloque D + v16.2 §7: si la sesión completada es el
      // TEST-VAM final de la recovery week (kind='final'), mostramos
      // mensaje motivacional específico en lugar del template estándar.
      // El campo `vam_test_kind` lo serializa el backend desde
      // sub-bloque 3 §8 (commit 760b2f2); el motor lo emite cuando
      // recovery_week.py genera test_vam con gate v16.2 §6 OK.
      const isFinalVamTest = result.session.vam_test_kind === "final";
      const toastMsg = isFinalVamTest
        ? t.plan.testVamSuccessToastFinal
        : result.hr_max !== null
          ? t.plan.testVamSuccessToastTemplate
              .replace("{pace}", result.pace_formatted)
              .replace("{hr}", String(result.hr_max))
          : t.plan.testVamSuccessToastNoHr.replace(
              "{pace}",
              result.pace_formatted,
            );
      showToast(toastMsg, "success");
      setVamModalOpen(false);
      setVamDistance("");
      setVamHrAvg("");
      setVamHrMax("");
      setVamPace("");
      // v14 §2: cierre del flujo — volver al panel semanal. La sesión test_vam
      // queda `completed` (lo marca el endpoint), así que al remontar PlanPage
      // hace un getActivePlan fresco y el gate (F16/F17) se desactiva (ya no
      // hay test_vam `planned`) → se muestra el plan completo.
      navigate("/plan");
    } catch (err) {
      if (err instanceof ApiError && err.code === "distance_meters_invalid") {
        setVamError(t.plan.testVamErrorDistance);
      } else if (
        err instanceof ApiError
        && (err.code === "hr_avg_required" || err.code === "hr_max_required")
      ) {
        setVamError(t.plan.testVamErrorHrRequired);
      } else if (
        err instanceof ApiError
        && (err.code === "hr_avg_invalid" || err.code === "hr_max_invalid")
      ) {
        setVamError(t.plan.testVamErrorHrRange);
      } else if (err instanceof ApiError && err.code === "avg_pace_invalid") {
        setVamError(t.plan.testVamErrorPace);
      } else {
        showToast(t.plan.toastError, "error");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onConfirmSkip() {
    if (!session) return;
    setSubmitting(true);
    try {
      const result = await patchSession(session.id, {
        action: "skip",
        // Sprint 12 S12.7: reason fijo "physical" — el modal solo
        // cubre el caso dolor/lesión tras la consolidación v9.2 §1.
        reason: "physical",
        notes: skipNotes || undefined,
      });
      setSession(result.session);
      const adaptation = result.adaptation?.adaptation;
      const alert = result.adaptation?.alert;
      if (alert) {
        showToast(t.plan.toastSkipAlertConsecutive, "error");
      } else if (adaptation === "next_intensity_to_z2") {
        showToast(t.plan.toastSkipPhysicalAdapted, "success");
      } else {
        showToast(t.plan.toastSessionSkipped, "success");
      }
      setSkipModalOpen(false);
    } catch {
      showToast(t.plan.toastError, "error");
    } finally {
      setSubmitting(false);
    }
  }

  /** Sprint 10 S10.1b: handler del modal "Posponer 1 día".
   *
   * El backend (S10.1) hace toda la lógica: cascada +1 día, auto-skip
   * hasta 3 sesiones de menor impacto para preservar race_date, o
   * rollback con 409 si irresoluble. Aquí solo enviamos el action y
   * mapeamos response/error a feedback al atleta.
   *
   * Tras éxito: refresh del plan para que la nueva session_date (target
   * desplazado) y eventuales sesiones marcadas `skipped` por el
   * auto-skip se reflejen en la UI.
   *
   * Tras error: mantenemos el modal abierto y mostramos el mensaje
   * inline. Los códigos del backend están listados en
   * backend/services/training_engine/postpone.py + el endpoint
   * /training-plans/active/sessions/<id>. */
  async function onConfirmPostpone() {
    if (!session) return;
    setSubmitting(true);
    setPostponeError(null);
    try {
      const result = await patchSession(session.id, { action: "postpone" });
      setSession(result.session);
      const postpone = result.postpone;
      if (postpone) {
        const autoSkippedCount = postpone.auto_skipped_ids.length;
        const msg = autoSkippedCount > 0
          ? t.plan.postponeSuccessWithSkips
              .replace("{sessions_shifted}", String(postpone.sessions_shifted))
              .replace("{sessions_skipped_auto}", String(autoSkippedCount))
          : t.plan.postponeSuccessSimple
              .replace("{sessions_shifted}", String(postpone.sessions_shifted));
        showToast(msg, "success");
      } else {
        // Fallback defensivo — no debería ocurrir en S10.1 backend pero
        // evita una UI silenciosa si la response llegara sin postpone.
        showToast(t.plan.postponeSuccessSimple.replace("{sessions_shifted}", "1"), "success");
      }
      setPostponeModalOpen(false);
      // Refresh completo del plan: la cascada pudo tocar múltiples
      // sesiones y eventuales auto-skips marcaron otras como skipped.
      void refresh();
    } catch (err) {
      const errorMsg = mapPostponeError(err, planMeta?.raceDate ?? null);
      setPostponeError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }

  /** Sprint 10 S10.2b: handler del modal "Reportar fatiga".
   *
   * Backend (S10.2) marca original skipped+skip_reason='fatigue' y
   * crea una sub `rod_z2_corto` 30 min Z2 en mismo session_date. La
   * UI tras éxito navega al detalle de la sub — es la que el atleta
   * verá/completará. La original queda visible en WeekView con badge
   * "Saltada por fatiga".
   *
   * Errores mapeados a copy específico (mapFatigueError). 409
   * already_reported_fatigue NO mantiene modal abierto con error: el
   * atleta puede cerrar y ver la sub (ya creada en una llamada previa). */
  async function onConfirmFatigue() {
    if (!session) return;
    setSubmitting(true);
    setFatigueError(null);
    try {
      const result = await patchSession(session.id, { action: "report_fatigue" });
      setSession(result.session);
      showToast(t.plan.fatigueSuccessToast, "success");
      setFatigueModalOpen(false);
      // Navegar al detalle de la sustituta — es la sesión que el
      // atleta debe ver/hacer. La original queda en el plan con badge
      // "Saltada por fatiga" (visible al volver al WeekView).
      if (result.fatigue_substitute) {
        navigate(`/plan/sesion/${result.fatigue_substitute.id}`);
      } else {
        // Defensa: si el backend no devolvió sub (no debería en S10.2),
        // hacemos refresh para que la UI no quede stale.
        void refresh();
      }
    } catch (err) {
      setFatigueError(mapFatigueError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main
        data-testid="session-loading"
        className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone"
      >
        <p className="text-sm text-bone-muted">{t.plan.detailTitle}…</p>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone">
        <h1 className="text-lg text-bone">Sesión no encontrada</h1>
        <Link to="/plan" className="text-sm text-lime hover:underline">
          ← Volver al plan
        </Link>
      </main>
    );
  }

  const isRest = session.session_type === "rest";
  const isPlanned = session.completion_status === "planned";
  const isTestVam = session.session_type === "test_vam";
  // Sprint 10 S10.2b: ventana de validez del botón "Reportar fatiga".
  // Backend solo acepta today o today+1 (FATIGUE_REPORT_WINDOW_DAYS=1).
  // La UI defensivamente desactiva el botón fuera de esa ventana con
  // tooltip específico por caso (test_vam / rest / past / far_future).
  const todayIso = _todayIso();
  const tomorrowIso = _addDaysIso(todayIso, 1);
  const isPast = session.session_date < todayIso;
  const isFarFuture = session.session_date > tomorrowIso;
  // Razón concreta de disable para tooltip + tests. null → habilitado.
  const fatigueDisabledReason: string | null = isTestVam
    ? t.plan.fatigueButtonTooltipDisabledTestVam
    : isRest
      ? t.plan.fatigueButtonTooltipDisabledRest
      : isPast
        ? t.plan.fatigueButtonTooltipDisabledPast
        : isFarFuture
          ? t.plan.fatigueButtonTooltipDisabledFarFuture
          : null;
  const fatigueDisabled = fatigueDisabledReason !== null;
  const typeLabel = t.plan[SESSION_TYPE_LABELS[session.session_type] as keyof typeof t.plan];
  // Sprint 8 S8.2 (UX-006): badge con estado visual derivado (incluye
  // `overdue` para planned + fecha pasada) + icono Unicode.
  const visualState = getSessionVisualState(session);
  const statusLabel = t.plan[
    VISUAL_STATE_I18N_KEY[visualState] as keyof typeof t.plan
  ];
  const statusIcon = VISUAL_STATE_ICONS[visualState];
  const structure = session.session_structure_json ?? {};
  const dateLabel = new Date(session.session_date).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <main className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 pb-32 text-bone md:pb-6">
      <header>
        <Link to="/plan" className="text-xs text-bone-muted hover:text-bone">
          ← {t.plan.pageTitle}
        </Link>
        <h1 className="mt-4 text-2xl font-medium tracking-tight">
          {typeLabel}
          {/* v16.1 §8 sub-bloque 3: mismo helper que SessionCard. */}
          {isTestVam && labelForVamTestKind(session.vam_test_kind)}
        </h1>
        <p className="mt-1 text-sm capitalize text-bone-muted">{dateLabel}</p>
        {!isRest && (
          <div className="mt-3 flex items-center gap-2 text-xs text-bone-muted">
            <span
              data-testid="detail-status-badge"
              data-visual-state={visualState}
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
                VISUAL_STATE_BADGE_CLASSES[visualState]
              }`}
            >
              {statusIcon && (
                <span aria-hidden="true" className="text-[10px] leading-none">
                  {statusIcon}
                </span>
              )}
              <span>{statusLabel}</span>
            </span>
            {session.intensity_zone && session.intensity_zone !== "mixed" && (
              <>
                <span>·</span>
                <span>
                  {t.plan.intensity}: {session.intensity_zone}
                </span>
              </>
            )}
            {/* v14 §3: ritmo /km tras la zona. Gate de 2 capas: VAM (ya
                aplicado en backend → pace_range null si no hay) + has_watch
                (aquí; hasWatch puede ser null si el fetch falló → no se
                muestra). String ya formateado por el backend. */}
            {session.pace_range && hasWatch === true && (
              <>
                <span>·</span>
                <span>{session.pace_range}</span>
              </>
            )}
          </div>
        )}
      </header>

      {/* v16.3 §4 — Superficie recomendada (etiqueta informativa). Null
          si plan_surface no resoluble, session_type='rest', o tipo no
          mapeado en la tabla §4.2. El reparto mod-4 caco_plus en plan
          "Ambos" se decide en backend según week_number. */}
      {session.recommended_surface && (
        <section
          data-testid="session-surface-recommendation"
          className="rounded-md border border-border-subtle bg-carbon-raised px-4 py-3"
        >
          <h3 className="text-xs uppercase tracking-widest text-gray-mid">
            {t.plan.surfaceRecommendedLabel}
          </h3>
          <p className="mt-1 text-sm text-bone">
            {labelForRecommendedSurface(session.recommended_surface)}
          </p>
        </section>
      )}

      {/* Targets */}
      {!isRest && (session.distance_target_km || session.duration_target_min) && (
        <section className="grid grid-cols-2 gap-2">
          {session.distance_target_km !== null && (
            <div className="rounded-md border border-border-subtle bg-carbon-raised p-3">
              <p className="text-xs text-gray-mid">{t.plan.targetDistance}</p>
              <p className="mt-1 text-base text-bone">
                {session.distance_target_km} km
              </p>
            </div>
          )}
          {session.duration_target_min !== null && (
            <div className="rounded-md border border-border-subtle bg-carbon-raised p-3">
              <p className="text-xs text-gray-mid">{t.plan.targetDuration}</p>
              <p className="mt-1 text-base text-bone">
                {session.duration_target_min} min
              </p>
            </div>
          )}
        </section>
      )}

      {/* Sprint 11 S11.3 (motor v8 §2): para test_vam, el structure_json
          del catálogo (S6/S9.5) queda obsoleto. Renderizamos el bloque
          v8 con hero + 6 pasos + consejo. Resto de session_types
          mantiene el render histórico de structure warmup/main/cooldown. */}
      {isTestVam ? (
        <TestVamProtocolBlock session={session} />
      ) : (
      <section
        data-testid="session-structure"
        className="flex flex-col gap-3 rounded-md border border-border-subtle bg-carbon-raised p-4"
      >
        {structure.warmup && (
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-mid">
              {t.plan.structureWarmup}
            </p>
            <p className="mt-1 text-sm text-bone">{structure.warmup}</p>
          </div>
        )}
        {structure.main && (
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-mid">
              {t.plan.structureMain}
            </p>
            {/* v16.4.1 §T6: respeta saltos de línea del helper z2_fartlek
                estructural (multi-línea bloque-a-bloque). */}
            <p
              data-testid="session-structure-main"
              className="mt-1 whitespace-pre-line text-sm text-bone"
            >
              {structure.main}
            </p>
          </div>
        )}
        {structure.cooldown && (
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-mid">
              {t.plan.structureCooldown}
            </p>
            <p className="mt-1 text-sm text-bone">{structure.cooldown}</p>
          </div>
        )}
      </section>
      )}

      {/* Activity linkeada (Bloque 5+ enriquece con datos reales) */}
      {session.actual_activity_id && (
        <section
          data-testid="actual-activity"
          className="rounded-md border border-success/30 bg-success/10 p-3 text-sm text-bone"
        >
          <p className="text-xs uppercase tracking-widest text-success">
            {t.plan.actualActivityTitle}
          </p>
          <p className="mt-1 text-bone-muted">
            Actividad #{session.actual_activity_id} (de Strava)
          </p>
        </section>
      )}

      {/* v14_2 §8: resultado reportado manualmente (rama SIN Strava).
          Lee las columnas propias de la sesión (para sesiones con Strava
          el bloque "Tu actividad" de arriba cubre el caso). Solo se
          muestra si hay al menos un campo reportado. */}
      {(session.actual_avg_hr != null
        || session.actual_max_hr != null
        || session.actual_avg_pace_seconds != null
        || session.perceived_effort != null
        || session.feeling != null) && (
        <section
          data-testid="post-session-result"
          className="rounded-md border border-border-subtle bg-carbon-raised p-3 text-sm text-bone"
        >
          <p className="text-xs uppercase tracking-widest text-gray-mid">
            {t.plan.postSessionResultTitle}
          </p>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-bone-muted">
            {session.actual_avg_hr != null && (
              <div className="flex justify-between">
                <dt>{t.plan.resultHrAvg}</dt>
                <dd className="text-bone">{session.actual_avg_hr} ppm</dd>
              </div>
            )}
            {session.actual_max_hr != null && (
              <div className="flex justify-between">
                <dt>{t.plan.resultHrMax}</dt>
                <dd className="text-bone">{session.actual_max_hr} ppm</dd>
              </div>
            )}
            {session.actual_avg_pace_seconds != null && (
              <div className="flex justify-between">
                <dt>{t.plan.resultPace}</dt>
                <dd className="text-bone">
                  {_formatPaceSeconds(session.actual_avg_pace_seconds)} /km
                </dd>
              </div>
            )}
            {session.perceived_effort != null && (
              <div className="flex justify-between">
                <dt>{t.plan.resultEffort}</dt>
                <dd className="text-bone">{session.perceived_effort}/5</dd>
              </div>
            )}
            {session.feeling != null && (
              <div className="flex justify-between">
                <dt>{t.plan.resultFeeling}</dt>
                <dd className="text-bone">
                  {t.plan[
                    (FEELING_OPTIONS.find((o) => o.value === session.feeling)
                      ?.labelKey ?? "feelingBien") as keyof typeof t.plan
                  ]}
                </dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {/* Notas previas */}
      {session.notes && (
        <section className="text-xs text-bone-muted">
          Notas: {session.notes}
        </section>
      )}

      {/* Acciones — TEST-VAM tiene su propio CTA "Reportar resultado".
           Sprint 10 S10.1b: el botón "Posponer" se renderiza siempre
           para test_vam pero DISABLED con tooltip — el backend lo
           rechaza con `cannot_postpone_test_vam` así que mejor evitar
           que el atleta lo intente.
           Sprint 10 S10.2b: ídem para "Reportar fatiga" sobre test_vam. */}
      {isPlanned && isTestVam && (
        <section
          data-testid="session-test-vam-actions"
          className="mt-2 flex flex-col gap-3"
        >
          <button
            type="button"
            data-testid="vam-report-btn"
            onClick={() => setVamModalOpen(true)}
            disabled={submitting}
            className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.plan.testVamReportButton}
          </button>
          <button
            type="button"
            data-testid="session-postpone-btn"
            disabled
            title={t.plan.postponeButtonDisabledTestVam}
            aria-label={t.plan.postponeButtonDisabledTestVam}
            className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted opacity-50 disabled:cursor-not-allowed"
          >
            {t.plan.postponeButton}
          </button>
          <button
            type="button"
            data-testid="session-fatigue-btn"
            disabled
            title={t.plan.fatigueButtonTooltipDisabledTestVam}
            aria-label={t.plan.fatigueButtonTooltipDisabledTestVam}
            className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted opacity-50 disabled:cursor-not-allowed"
          >
            {t.plan.fatigueButton}
          </button>
        </section>
      )}

      {/* Acciones — sesiones normales (no rest, no test_vam, planned).
           Sprint 10 S10.1b: añadido botón "Posponer" junto a "Skip".
           Estilo secundario neutro (no rojo) para distinguir de skip. */}
      {isPlanned && !isRest && !isTestVam && (
        <section className="mt-2 flex flex-col gap-3">
          <textarea
            placeholder={t.plan.notesPlaceholder}
            value={completeNotes}
            onChange={(e) => setCompleteNotes(e.target.value)}
            className="rounded-md border border-border-subtle bg-carbon-raised p-2 text-sm text-bone placeholder:text-gray-mid"
            rows={2}
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="session-complete-btn"
              onClick={onCompleteClick}
              disabled={submitting}
              className="hidden flex-1 rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50 md:block"
            >
              {t.plan.completeButton}
            </button>
            <button
              type="button"
              data-testid="session-postpone-btn"
              onClick={() => {
                setPostponeError(null);
                setPostponeModalOpen(true);
              }}
              disabled={submitting}
              title={t.plan.postponeButtonTooltip}
              className="rounded-md border border-warning/40 px-4 py-2 text-sm text-warning hover:border-warning hover:bg-warning/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.plan.postponeButton}
            </button>
            <button
              type="button"
              data-testid="session-fatigue-btn"
              onClick={() => {
                setFatigueError(null);
                setFatigueModalOpen(true);
              }}
              disabled={submitting || fatigueDisabled}
              title={fatigueDisabledReason ?? undefined}
              aria-label={fatigueDisabledReason ?? t.plan.fatigueButton}
              className="rounded-md border border-warning/40 px-4 py-2 text-sm text-warning hover:border-warning hover:bg-warning/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.plan.fatigueButton}
            </button>
            <button
              type="button"
              data-testid="session-skip-btn"
              onClick={() => setSkipModalOpen(true)}
              disabled={submitting}
              className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.plan.skipButton}
            </button>
          </div>
        </section>
      )}

      {/* TEST-VAM modal */}
      {vamModalOpen && (
        <div
          data-testid="vam-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vam-modal-title"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
        >
          <div className="my-auto max-h-[90vh] w-full max-w-sm overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-carbon-raised p-5">
            <h2
              id="vam-modal-title"
              className="text-lg font-medium text-bone"
            >
              {t.plan.testVamModalTitle}
            </h2>
            <p className="mt-1 text-sm text-bone-muted">
              {t.plan.testVamModalIntro}
            </p>
            <div className="mt-4 flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.testVamDistanceLabel}
                </span>
                <input
                  data-testid="vam-distance-input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  placeholder={t.plan.testVamDistancePlaceholder}
                  value={vamDistance}
                  onChange={(e) => setVamDistance(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.testVamHrAvgLabel}
                </span>
                <input
                  data-testid="vam-hr-avg-input"
                  type="number"
                  inputMode="numeric"
                  min={40}
                  max={230}
                  placeholder={t.plan.testVamHrAvgPlaceholder}
                  value={vamHrAvg}
                  onChange={(e) => setVamHrAvg(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.testVamHrMaxLabel}
                </span>
                <input
                  data-testid="vam-hr-max-input"
                  type="number"
                  inputMode="numeric"
                  min={40}
                  max={230}
                  placeholder={t.plan.testVamHrMaxPlaceholder}
                  value={vamHrMax}
                  onChange={(e) => setVamHrMax(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
                />
              </label>
              {/* v11 §1: ritmo medio OPCIONAL (mm:ss). */}
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.testVamPaceLabel}
                </span>
                <input
                  data-testid="vam-pace-input"
                  type="text"
                  // v14 §1: teclado de TEXTO (no "numeric") para poder teclear
                  // los ':' del formato mm:ss en móvil. El parsing
                  // _parsePaceMmSs y el payload (avg_pace_seconds_per_km) no
                  // cambian.
                  inputMode="text"
                  placeholder={t.plan.testVamPacePlaceholder}
                  value={vamPace}
                  onChange={(e) => setVamPace(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
                />
                <span className="text-xs text-gray-mid">
                  {t.plan.testVamPaceHelper}
                </span>
              </label>
            </div>
            {vamError && (
              <p
                data-testid="vam-modal-error"
                role="alert"
                className="mt-2 text-sm text-danger"
              >
                {vamError}
              </p>
            )}
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setVamModalOpen(false)}
                disabled={submitting}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
              >
                {t.plan.testVamCancelButton}
              </button>
              <button
                type="button"
                data-testid="vam-modal-submit"
                onClick={() => void onSubmitVam()}
                disabled={submitting}
                className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.testVamSubmitButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skip modal */}
      {skipModalOpen && (
        <div
          data-testid="skip-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="skip-modal-title"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
        >
          <div className="my-auto max-h-[90vh] w-full max-w-sm overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-carbon-raised p-5">
            <h2
              id="skip-modal-title"
              className="text-lg font-medium text-bone"
            >
              {t.plan.skipModalTitle}
            </h2>
            {/* Sprint 12 S12.7 (v9.2 §1): modal mono-opción. Antes
                había un fieldset con 2 radios (logistics/physical);
                tras la consolidación solo queda el caso físico
                (dolor/lesión). La selección logística se redirige al
                flujo Posponer/Mover. */}
            <div
              data-testid="skip-modal-physical-copy"
              className="mt-4 rounded-md border border-border-subtle p-3"
            >
              <p className="text-sm text-bone">{t.plan.skipModalPhysical}</p>
              <p className="mt-1 text-xs text-bone-muted">
                {t.plan.skipModalPhysicalHint}
              </p>
            </div>
            <textarea
              placeholder={t.plan.notesPlaceholder}
              value={skipNotes}
              onChange={(e) => setSkipNotes(e.target.value)}
              className="mt-3 w-full rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
              rows={2}
            />
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setSkipModalOpen(false)}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
              >
                {t.plan.skipModalCancel}
              </button>
              <button
                type="button"
                data-testid="skip-modal-confirm"
                onClick={() => void onConfirmSkip()}
                disabled={submitting}
                className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.skipModalConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint 10 S10.1b: Postpone modal. SIN razón ni notas (v7.1 §8
           "sin pedir motivo"). El warning de race_modification se
           renderiza solo si el plan tiene carrera real (no
           personal_challenge). */}
      {postponeModalOpen && (
        <div
          data-testid="postpone-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="postpone-modal-title"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
        >
          <div className="my-auto max-h-[90vh] w-full max-w-sm overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-carbon-raised p-5">
            <h2
              id="postpone-modal-title"
              className="text-lg font-medium text-bone"
            >
              {t.plan.postponeModalTitle}
            </h2>
            <p className="mt-3 text-sm text-bone-muted">
              {t.plan.postponeModalBody}
            </p>
            {planMeta && planMeta.raceDistance !== "personal_challenge" && (
              <p
                data-testid="postpone-modal-race-warning"
                className="mt-3 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning"
              >
                {t.plan.postponeModalRaceWarning}
              </p>
            )}
            {postponeError && (
              <p
                data-testid="postpone-modal-error"
                role="alert"
                className="mt-3 text-sm text-danger"
              >
                {postponeError}
              </p>
            )}
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                data-testid="postpone-modal-cancel"
                onClick={() => {
                  setPostponeModalOpen(false);
                  setPostponeError(null);
                }}
                disabled={submitting}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.postponeModalCancelButton}
              </button>
              <button
                type="button"
                data-testid="postpone-modal-confirm"
                onClick={() => void onConfirmPostpone()}
                disabled={submitting}
                className="rounded-md bg-warning px-4 py-2 text-sm font-medium text-graphite hover:bg-warning/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.postponeModalConfirmButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint 10 S10.2b: Fatigue modal. SIN razón ni notas (v7.1 §9
           "sin pedir motivo"). El sub-copy se adapta a has_watch del
           profile: con reloj → Z2 cuantitativo; sin reloj → "ritmo
           conversacional" cualitativo. Si has_watch es null (fetch
           falló o profile incompleto), usamos el copy sin reloj como
           default conservador (no asume datos que no tenemos). */}
      {fatigueModalOpen && (
        <div
          data-testid="fatigue-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fatigue-modal-title"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
        >
          <div className="my-auto max-h-[90vh] w-full max-w-sm overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-carbon-raised p-5">
            <h2
              id="fatigue-modal-title"
              className="text-lg font-medium text-bone"
            >
              {t.plan.fatigueModalTitle}
            </h2>
            <p className="mt-3 text-sm text-bone-muted">
              {t.plan.fatigueModalBody}
            </p>
            <p
              data-testid="fatigue-modal-warning"
              className="mt-3 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning"
            >
              {t.plan.fatigueModalWarning}
            </p>
            <p
              data-testid="fatigue-modal-subcopy"
              className="mt-3 text-sm text-bone"
            >
              {hasWatch === true
                ? t.plan.fatigueModalSubCopyWithWatch
                : t.plan.fatigueModalSubCopyNoWatch}
            </p>
            {fatigueError && (
              <p
                data-testid="fatigue-modal-error"
                role="alert"
                className="mt-3 text-sm text-danger"
              >
                {fatigueError}
              </p>
            )}
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                data-testid="fatigue-modal-cancel"
                onClick={() => {
                  setFatigueModalOpen(false);
                  setFatigueError(null);
                }}
                disabled={submitting}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.fatigueModalCancelButton}
              </button>
              <button
                type="button"
                data-testid="fatigue-modal-confirm"
                onClick={() => void onConfirmFatigue()}
                disabled={submitting}
                className="rounded-md bg-warning px-4 py-2 text-sm font-medium text-graphite hover:bg-warning/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.fatigueModalConfirmButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v14_2 §8: modal post-sesión (rama SIN Strava). 5 campos opcionales. */}
      {postSessionModalOpen && (
        <div
          data-testid="post-session-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="post-session-modal-title"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
        >
          <div className="my-auto max-h-[90vh] w-full max-w-sm overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-carbon-raised p-5">
            <h2
              id="post-session-modal-title"
              className="text-lg font-medium text-bone"
            >
              {t.plan.postSessionModalTitle}
            </h2>
            <p className="mt-1 text-sm text-bone-muted">
              {t.plan.postSessionModalIntro}
            </p>
            <div className="mt-4 flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.postSessionHrAvgLabel}
                </span>
                <input
                  data-testid="ps-hr-avg-input"
                  type="number"
                  inputMode="numeric"
                  min={40}
                  max={230}
                  value={psHrAvg}
                  onChange={(e) => setPsHrAvg(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.postSessionHrMaxLabel}
                </span>
                <input
                  data-testid="ps-hr-max-input"
                  type="number"
                  inputMode="numeric"
                  min={40}
                  max={230}
                  value={psHrMax}
                  onChange={(e) => setPsHrMax(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.postSessionPaceLabel}
                </span>
                <input
                  data-testid="ps-pace-input"
                  type="text"
                  inputMode="text"
                  placeholder={t.plan.postSessionPacePlaceholder}
                  value={psPace}
                  onChange={(e) => setPsPace(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-border-subtle bg-graphite p-2 text-sm text-bone placeholder:text-gray-mid"
                />
              </label>
              {/* Esfuerzo percibido: selector segmentado 1-5 (no slider).
                  s53.2: componente compartido con el mini-modal Marcar. */}
              <EffortSelector
                value={psEffort}
                onChange={setPsEffort}
                label={t.plan.postSessionEffortLabel}
                testIdPrefix="ps-effort"
              />
              {/* Sensaciones: selector 4 opciones. */}
              <div className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-widest text-gray-mid">
                  {t.plan.postSessionFeelingLabel}
                </span>
                <div className="grid grid-cols-2 gap-2" role="group" aria-label={t.plan.postSessionFeelingLabel}>
                  {FEELING_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`ps-feeling-${opt.value}`}
                      aria-pressed={psFeeling === opt.value}
                      onClick={() =>
                        setPsFeeling(psFeeling === opt.value ? null : opt.value)
                      }
                      className={`rounded-md border px-2 py-2 text-sm transition-colors duration-fast ${
                        psFeeling === opt.value
                          ? "border-lime bg-lime/15 text-lime"
                          : "border-border-subtle text-bone-muted hover:border-border-strong"
                      }`}
                    >
                      {t.plan[opt.labelKey as keyof typeof t.plan]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {postSessionError && (
              <p
                data-testid="post-session-error"
                role="alert"
                className="mt-2 text-sm text-danger"
              >
                {postSessionError}
              </p>
            )}
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                data-testid="post-session-close"
                onClick={onClosePostSession}
                disabled={submitting}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
              >
                {t.plan.postSessionCloseButton}
              </button>
              <button
                type="button"
                data-testid="post-session-save"
                onClick={() => void onSavePostSession()}
                disabled={submitting}
                className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.postSessionSaveButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-bloque E.2: sticky bottom mobile-only para "Marcar completada".
           Reduce friction: atletas no tienen que scrollear hasta el final
           del detalle (warmup + main + cooldown + notas + textarea) para
           confirmar la sesión. Mismas condiciones de render que el botón
           principal scroll-position; el sticky NO duplica postpone/fatigue
           (acciones secundarias que sí justifican llegar al final).
           s64: se apila justo ENCIMA de la barra de navegación inferior
           (bottom-nav = su altura; pb-safe-nav = 1rem si la barra ya
           cubre el inset). Sin barra, idéntico a antes. */}
      {isPlanned && !isRest && !isTestVam && (
        <div
          className="fixed inset-x-0 bottom-nav z-40 border-t border-border-subtle bg-graphite px-4 pt-3 pb-safe-nav md:hidden"
        >
          <button
            type="button"
            data-testid="session-complete-btn-sticky"
            onClick={onCompleteClick}
            disabled={submitting}
            className="w-full rounded-md bg-lime px-4 py-3 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.plan.completeButton}
          </button>
        </div>
      )}

      {/* Toast stack */}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          className="pointer-events-none fixed bottom-toast right-4 z-50 flex max-w-xs flex-col gap-2"
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              data-testid={`toast-${toast.kind}`}
              className="pointer-events-auto rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone"
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}

    </main>
  );
}
